// @flow

import Config from '@insolus/config';
import Cookies from '@insolus/cookies';
import staticRedirect from 'utils/staticRedirect';
import SessionApi from './api/session';
import { observable, computed, action, runInAction } from 'mobx';

import type { Store } from '@insolus/stores';
import type { TokenUser, CurrentUser, UserGroup } from './api/session';
import type { PublishData } from 'stores/api/ads';
import type { Advertiser } from './api/advertisers';

const _setSessionCookie = (token: string) => {
    const { cookie, timeout } = Config.get('session');

    const cookieConfig = {};
    if (timeout) {
        cookieConfig.expires = timeout;
    }

    Cookies.set(cookie, token, cookieConfig);
};

class AuthStore implements Store {
    @observable isFetched: boolean = false;
    @observable isBusy: boolean = false;
    @observable user: CurrentUser | null = null;

    @computed get isLogged(): boolean {
        return !!this.user;
    }

    @computed get displayName(): string | null {
        if (!this.user) {
            return null;
        }

        const { email } = this.user;

        return email;
    }

    @computed get email(): string | null {
        return this.user?.email ?? null;
    }

    init() {
        this.fetchUser();
    }

    // ------------------------------------------------------
    // -
    // -    Boolean methods
    // -
    // ------------------------------------------------------

    is(group: UserGroup): boolean {
        return !!this.user && this.user.group === group;
    }

    // ------------------------------------------------------
    // -
    // -    Actions
    // -
    // ------------------------------------------------------

    @action
    async fetchUser(): Promise<any> {
        if (this.isBusy) {
            throw new Error(`Un opération est déjà en cours.`);
        }

        if (!Cookies.get(Config.get('session.cookie'))) {
            this.isFetched = true;
            return;
        }

        this.isBusy = true;

        try {
            const user: CurrentUser = await SessionApi.fetch();
            runInAction(() => {
                this.user = user;
            });
        } catch (error) {
            // - Non connecté.
            if (error.httpCode === 401 /* Unauthorized */) {
                this.logout();
            } else {
                // eslint-disable-next-line no-console
                console.error('Error:', error.message || error.code);
            }
        } finally {
            runInAction(() => {
                this.isBusy = false;
                this.isFetched = true;
            });
        }
    }

    @action
    async login(
        email: string,
        password: string,
        redirect?: boolean = true,
        draft?: PublishData | null = null
    ): Promise<any> {
        if (this.isBusy) {
            throw new Error(`Une opération est déjà en cours.`);
        }
        this.isBusy = true;

        try {
            const user: TokenUser = await SessionApi.login(email, password, draft);
            runInAction(() => {
                const { token, ...otherUser } = user;
                this.user = otherUser;
            });
            _setSessionCookie(user.token);

            if (redirect) {
                let redirectUrl = '/admin';
                if (user.group !== 'admin') {
                    redirectUrl = user.draftId ? `publish/${user.draftId}/depict` : '/account';
                }

                staticRedirect(redirectUrl, { replace: true });
            }
        } finally {
            runInAction(() => {
                this.isBusy = false;
            });
        }
    }

    @action logout() {
        Cookies.remove(Config.get('session.cookie'));
        this.user = null;
        staticRedirect('/');
    }

    @action setProfile(profile: Advertiser) {
        runInAction(() => {
            if (!this.user) {
                return;
            }

            this.user.profile = profile;
        });
    }

    @action setCredits(credits: number) {
        runInAction(() => {
            if (!this.user) {
                return;
            }

            this.user.credits = credits;
        });
    }
}

export default (new AuthStore(): AuthStore);
export { AuthStore };
